import { type QueryClient, useSuspenseQuery } from '@tanstack/react-query'
import {
	type ClientSession,
	clientSessionQueryOptions
} from '@/query/query-options'

/**
 * The session below the gate, narrowed to the arm that carries a user.
 *
 * NOT A SECOND GUARD: `(private)/route.tsx` has already redirected an
 * anonymous caller to `/login`. What a redirect cannot do is narrow the union
 * a child reads for itself, which `routing.md` names as the one case where the
 * `sessionType` check is not redundant. This is that check, written once.
 *
 * It throws rather than redirecting, and the branch is meant to be dead:
 * reaching it means the gate is gone, which is our defect and belongs on the
 * error boundary rather than dressed up as a signed-out user. Passing the
 * session down through route context would remove the call entirely — and
 * break the third property `routing.md` credits for the substitution having
 * cost one file.
 */
export async function requireAuthenticatedSession(
	queryClient: QueryClient
): Promise<AuthenticatedSession> {
	// The gate above resolved this, so it is a cache read rather than a fetch.
	const session = await queryClient.ensureQueryData(
		clientSessionQueryOptions()
	)

	if (session.sessionType !== 'authenticated') {
		throw new Error(BELOW_THE_GATE)
	}

	return session
}

/**
 * The same narrowing, for a component instead of a `beforeLoad`.
 *
 * Two entry points because the two callers cannot share one: a guard runs
 * before React and awaits, a render cannot await and must subscribe. What they
 * do share is the branch that must never be taken and the message it carries,
 * which is the half worth writing once.
 *
 * Suspends rather than reading through the cache by hand: the gate resolved
 * this session with `fetchQuery`, so the entry is there and nothing waits —
 * and staying subscribed is what makes a sign-out reach the screen instead of
 * leaving a snapshot behind.
 */
export function useAuthenticatedSession(): AuthenticatedSession {
	const { data: session } = useSuspenseQuery(clientSessionQueryOptions())

	if (session.sessionType !== 'authenticated') {
		throw new Error(BELOW_THE_GATE)
	}

	return session
}

const BELOW_THE_GATE =
	'An authenticated session was required below the session gate, which admits no other kind.'

/** Derived from the contract, so the two cannot drift. */
type AuthenticatedSession = Extract<
	ClientSession,
	{ sessionType: 'authenticated' }
>
